import React, { useEffect, useState, useCallback } from 'react';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import {
    LastCargoesReportData,
    ManifestReportData,
    REPORT_TYPE,
    ReportGenerationOptions,
    REPORT_TYPE_ORIENTATION,
    ReportGenerationRequest,
    ReportSubTypes,
    ReportSubType,
    ReportTypes,
    REPORT_TYPE_FILE_FORMAT,
    ReportData,
    ManifoldPlanReportData,
    TankplanReportData,
} from 'src/utils/reportGenerator/ReportGeneratorTypes';
import { useStowageStore } from 'src/context/StowageContext';
import { useVesselStore } from 'src/context/VesselContext';
import { useRotationStore } from 'src/context/RotationContext';
import { getPreviousDistinctCargoContentsInMultipleTanks } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { getLastBerthCallInPortCall } from 'src/utils/stowage/stowageRotationUtils';
import { generateReport } from 'src/utils/reportGenerator/reportGenerator';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import {
    createManifestReportFilename,
    createReportFilename,
    createTankPlanReport,
    getCargoContentInTanksList,
    getCargoInTanksList,
    getEffectiveManifestBerthCall,
    getTankIdsWhereCharterersHaveCargo,
} from 'src/utils/stowage/reportUtils';
import ReportOptionsSection, { CargoNameType, OutputAction } from './reportPanel/ReportOptionsSection';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import ReportGenerationProgress from './reportPanel/ReportGenerationProgress';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';
import { PanelDimensions } from 'src/types/ViewModel/Stowage/PanelDimensions';
import { TankPlanReportGenerationData, useTankPlanImageGenerator } from 'src/utils/stowage/reports/tankPlanImageGenerator';
import { useToast } from 'src/app/useToast';
import { isManualCargo } from 'src/utils/stowage/cargoOperationUtils';

interface PrintPanelProps {
    onClose: () => void;
}

const PageHeaderStowagePrintPanel: React.FC<PrintPanelProps> = ({ onClose }) => {
    const [selectedReports, setSelectedReports] = useState<Set<ReportSubType>>(new Set());
    const [selectedReportFileType, setSelectedReportFileType] = useState<'pdf' | 'xlsx' | null>(null);
    const [filterOnSelectedTanks, setFilterOnSelectedTanks] = useState(false);
    const [selectedCharterers, setSelectedCharterers] = useState<string[]>([]);
    const [lastCargoesNumberOfSteps, setLastCargoesNumberOfSteps] = useState(3);
    const [isGeneratingReports, setIsGeneratingReports] = useState(false);
    const [reportGenerationProgress, setReportGenerationProgress] = useState<string>('');
    const [allReports, setAllReports] = useState<ReportGenerationRequest[]>([]);
    const [tankPlanReports, setTankPlanReports] = useState<TankPlanReportGenerationData[]>([]);
    const [selectedOutputAction, setSelectedOutputAction] = useState<OutputAction>('Open');
    const [cargoNameType, setCargoNameType] = useState<CargoNameType>('tradeName');
    const [includeUnNo, setIncludeUnNo] = useState<boolean>(false);
    const [includeAllTanks, setIncludeAllTanks] = useState<boolean>(false);
    const [manualCargoesOnly, setManualCargoesOnly] = useState<boolean>(false);
    const [tanksWithManualCargoes, setTanksWithManualCargoes] = useState<number[]>([]);
    const [selectedBerthReportsBerthCall, setSelectedBerthReportsBerthCall] = useState<RotationBerthCall | null>(null);

    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const { vessel } = useVesselStore();
    const { activeStowagePlan, tankPlan, mouseClickPosition, setIsGeneratingTankPlanReport, isGeneratingTankPlanReport } = useStowageStore();
    const { selectedTanks, setSelectedTanks, setFilterTanksForReport } = useStowageSelectionStore();
    const { toastError } = useToast();

    const updateProgressMessage = useCallback((reportSubType?: ReportSubType, index?: number, total?: number): void => {
        if (!reportSubType) {
            setReportGenerationProgress('Finalizing reports...');
            return;
        }

        const _progressText = `Generating ${reportSubType.subType}${total ? ` (${index}/${total})` : ''}`;
        setReportGenerationProgress(_progressText);
    }, []);

    const {
        generateTankPlanReports,
        generatedReports,
        isGenerating: isGeneratingTankPlanImages,
    } = useTankPlanImageGenerator(updateProgressMessage, toastError);

    const generateReports = useCallback(async () => {
        const tankPlanElement = document.getElementById('tankPlanPdfReportContainer');
        generateTankPlanReports(tankPlanElement, tankPlanReports, Array.from(selectedReports)?.length);
    }, [generateTankPlanReports, tankPlanReports, selectedReports]);

    useEffect(() => {
        const tanks = filterOnSelectedTanks ? selectedTanks : vessel.cargoTanks;
        const cargoes = getCargoContentInTanksList(
            tankPlan,
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            null,
            tanks
        );
        const tanksWithManualCargoes = cargoes
            ?.filter((cargoContent) => isManualCargo(cargoContent.cargo))
            ?.map((cargoContent) => cargoContent.stowage.tankId);
        setTanksWithManualCargoes(tanksWithManualCargoes);
    }, [tankPlan, activeStowagePlan, filterOnSelectedTanks, selectedStowageRotationPortCall, selectedStowageRotationBerthCall, selectedTanks]);

    useEffect(() => {
        if (isGeneratingReports && tankPlanReports && tankPlanReports.length > 0) {
            generateReports();
        }
    }, [isGeneratingReports, tankPlanReports, generateReports]);

    useEffect(() => {
        if (manualCargoesOnly) {
            setSelectedCharterers([]);
            setSelectedTanks([]);
        }
    }, [manualCargoesOnly]);

    // Reset the shared Berth reports berth selection back to "no berth selected" whenever the
    // selected port call changes, so a stale berth from a previous port call isn't reused.
    useEffect(() => {
        setSelectedBerthReportsBerthCall(null);
    }, [selectedStowageRotationPortCall]);

    useEffect(() => {
        const isLastCargoesSelected = Array.from(selectedReports).some(
            (report) =>
                report.reportType === REPORT_TYPE.LAST_CARGOES ||
                report.reportType === REPORT_TYPE.LAST_CARGOES_FOSFA ||
                report.reportType === REPORT_TYPE.LAST_CARGOES_NOBL
        );

        if (!isLastCargoesSelected) {
            setManualCargoesOnly(false);
        }
    }, [selectedReports, setManualCargoesOnly]);

    useEffect(() => {
        if (!isGeneratingTankPlanImages && isGeneratingTankPlanReport) {
            if (generatedReports && generatedReports.length > 0) {
                const _tankPlanReports = generatedReports.map((report) => {
                    // Use the port/berth call captured on this specific report's baseData
                    // (set per-subtype scope in handleGenerateReport) rather than the outer
                    // Rotation-tree navigation state, so the filename and report header match
                    // whatever scope (whole port, or a specific berth) the image was actually
                    // captured with.
                    const reportOptions: ReportGenerationOptions = {
                        fileName: createReportFilename(
                            report.reportSubType,
                            vessel,
                            report.baseData.selectedPortCall,
                            report.baseData.selectedBerthCall
                        ),
                        orientation: REPORT_TYPE_ORIENTATION[report.reportSubType.reportType],
                    };

                    return {
                        reportType: report.reportSubType.reportType,
                        data: {
                            vessel,
                            headerTitle: report.reportSubType.headerTitle,
                            ...report.baseData,
                        } as TankplanReportData,
                        options: reportOptions,
                    } as ReportGenerationRequest;
                });
                setAllReports((prevReports) => [...prevReports, ..._tankPlanReports]);
            } else if (tankPlanReports.length > 0) {
                // Tank plan generation completed without producing any reports (e.g. element not found).
                // Unblock the UI by clearing the generating state.
                console.error('Tank plan image generation completed without producing reports.');
                toastError('Failed to generate tank plan reports. Please try again.');
            }
            setIsGeneratingTankPlanReport(false);
        }
    }, [generatedReports, isGeneratingTankPlanImages]);

    useEffect(() => {
        if (!isGeneratingTankPlanReport) {
            if (allReports.length > 0) {
                finalize(allReports);
            } else if (isGeneratingReports) {
                // No reports to finalize (all generation paths completed without output).
                // Reset the generating state to unblock the UI.
                setIsGeneratingReports(false);
            }
        }
    }, [allReports, isGeneratingTankPlanReport]);

    const handleGenerateReport = async (): Promise<void> => {
        setIsGeneratingReports(true);
        setReportGenerationProgress('Preparing reports...');

        let selectedTankIds: number[];

        if (manualCargoesOnly) {
            selectedTankIds = tanksWithManualCargoes;
        } else if (filterOnSelectedTanks) {
            selectedTankIds = selectedTanks.map((tank) => tank.tankId);
        } else if (selectedCharterers.length > 0) {
            selectedTankIds = getTankIdsWhereCharterersHaveCargo(
                tankPlan,
                activeStowagePlan,
                selectedStowageRotationPortCall,
                selectedStowageRotationBerthCall,
                selectedCharterers
            );
        }

        if (selectedTankIds && selectedTankIds.length > 0) {
            setFilterTanksForReport(true);
            setSelectedTanks(vessel.cargoTanks.filter((tank) => selectedTankIds.includes(tank.tankId)));
        } else {
            setFilterTanksForReport(false);
        }

        const otherReports: ReportGenerationRequest[] = [];
        const _tankPlanReports: TankPlanReportGenerationData[] = [];

        for (const reportSubType of Array.from(selectedReports)) {
            const fileName = createReportFilename(reportSubType, vessel, selectedStowageRotationPortCall, selectedStowageRotationBerthCall);
            const options: ReportGenerationOptions = {
                fileName: fileName,
                orientation: REPORT_TYPE_ORIENTATION[reportSubType.reportType],
            };
            let reportData: ReportData;

            switch (reportSubType.reportType) {
                case REPORT_TYPE.TANK_PLANS: {
                    setIsGeneratingTankPlanReport(true);
                    // Port-scoped Tank plan reports are always forced to the whole port call
                    // (no berth), regardless of the outer Rotation-tree navigation state.
                    // Berth-scoped Tank plan reports use the Berth reports tab's own shared
                    // berth selector instead of whatever berth the user currently has navigated
                    // to elsewhere in the app.
                    const effectiveTankPlanBerthCall =
                        reportSubType.reportScope === 'port' ? null : selectedBerthReportsBerthCall;
                    const tankPlanFileName = createReportFilename(reportSubType, vessel, selectedStowageRotationPortCall, effectiveTankPlanBerthCall);
                    const tankPlanReport = createTankPlanReport(
                        reportSubType,
                        vessel,
                        selectedStowageRotationPortCall,
                        effectiveTankPlanBerthCall,
                        tankPlanFileName
                    );
                    tankPlanReport.baseData.selectedCharterers = selectedCharterers;
                    if (tankPlanReport) {
                        _tankPlanReports.push(tankPlanReport);
                    }
                    break;
                }

                case REPORT_TYPE.MANIFESTS: {
                    const effectiveManifestBerthCall = getEffectiveManifestBerthCall(reportSubType, selectedBerthReportsBerthCall);
                    options.fileName = createManifestReportFilename(
                        reportSubType,
                        vessel,
                        selectedStowageRotationPortCall,
                        selectedBerthReportsBerthCall
                    );
                    reportData = {
                        reportType: REPORT_TYPE.MANIFESTS,
                        vessel: vessel,
                        selectedPortCall: selectedStowageRotationPortCall,
                        selectedBerthCall: effectiveManifestBerthCall,
                        cargoInTanksList: getCargoInTanksList(
                            tankPlan,
                            activeStowagePlan,
                            selectedStowageRotationPortCall,
                            effectiveManifestBerthCall,
                            reportSubType.tankPlanCondition,
                            selectedTankIds
                        ),
                        headerTitle: reportSubType.headerTitle,
                        selectedTankIds,
                    } as ManifestReportData;
                    break;
                }

                case REPORT_TYPE.MANIFOLD_PLAN: {
                    options.fileName = `${vessel?.vesselName}-Manifold Plan-${selectedStowageRotationPortCall?.voyageNumber}-${selectedStowageRotationPortCall?.portDisplayName}`;

                    let berthCall = selectedStowageRotationBerthCall;
                    if (selectedStowageRotationBerthCall) {
                        options.fileName += ` (${selectedStowageRotationBerthCall.berthName})`;
                    } else {
                        berthCall = getLastBerthCallInPortCall(selectedStowageRotationPortCall);
                    }

                    reportData = {
                        reportType: REPORT_TYPE.MANIFOLD_PLAN,
                        vessel: vessel,
                        selectedPortCall: selectedStowageRotationPortCall,
                        selectedBerthCall: berthCall,
                        cargoContent: getCargoContentInTanksList(
                            tankPlan,
                            activeStowagePlan,
                            selectedStowageRotationPortCall,
                            selectedStowageRotationBerthCall,
                            null
                        ),
                        selectedTankIds,
                    } as ManifoldPlanReportData;
                    break;
                }

                case REPORT_TYPE.LAST_CARGOES:
                case REPORT_TYPE.LAST_CARGOES_FOSFA:
                case REPORT_TYPE.LAST_CARGOES_NOBL: {
                    const isFosfa = reportSubType.reportType === REPORT_TYPE.LAST_CARGOES_FOSFA;
                    const isNobl = reportSubType.reportType === REPORT_TYPE.LAST_CARGOES_NOBL;
                    const fosfaMode: LastCargoesReportData['fosfaMode'] = isNobl ? 'nobl' : isFosfa ? 'approved' : 'off';
                    const fileNameSuffix = isFosfa ? '-FOSFA' : isNobl ? '-NOBL' : '';
                    options.fileName = `${vessel?.vesselName}-Last-Cargoes${fileNameSuffix}-${selectedStowageRotationPortCall?.voyageNumber}-${selectedStowageRotationPortCall?.portDisplayName}`;

                    let berthCall = selectedStowageRotationBerthCall;
                    if (selectedStowageRotationBerthCall) {
                        options.fileName += ` (${selectedStowageRotationBerthCall.berthName})`;
                    } else {
                        berthCall = getLastBerthCallInPortCall(selectedStowageRotationPortCall);
                    }

                    const cargoContents = getPreviousDistinctCargoContentsInMultipleTanks(
                        activeStowagePlan,
                        vessel.cargoTanks,
                        berthCall,
                        lastCargoesNumberOfSteps,
                        true // includeCurrentCargo
                    );

                    const cargoContentsPerTank = Array.from(cargoContents.keys()).map((tankId) => ({
                        tankId,
                        cargoContent: cargoContents.get(tankId) ?? [],
                    }));

                    reportData = {
                        reportType: reportSubType.reportType,
                        vessel: vessel,
                        stowagePlan: activeStowagePlan,
                        numberOfSteps: lastCargoesNumberOfSteps,
                        selectedBerthCall: selectedStowageRotationBerthCall,
                        selectedPortCall: selectedStowageRotationPortCall,
                        selectedTankIds,
                        cargoContentsPerTank,
                        cargoNameType: cargoNameType,
                        includeUnNo,
                        includeAllTanks,
                        fosfaMode,
                    } as LastCargoesReportData;
                    break;
                }

                default:
                    throw new Error(`Unsupported report type: ${reportSubType.reportType}`);
            }

            if (reportData && selectedCharterers && selectedCharterers.length > 0) {
                reportData.selectedCharterers = selectedCharterers;
            }

            if (reportSubType.reportType !== REPORT_TYPE.TANK_PLANS && reportData) {
                otherReports.push({
                    reportType: reportSubType.reportType,
                    data: reportData,
                    options,
                });
            }
        }

        setAllReports([...otherReports]);
        setTankPlanReports(_tankPlanReports);
        if (_tankPlanReports.length > 0) {
            setReportGenerationProgress('Generating tank plan reports...');
        }
    };

    const finalize = async (_allReports: ReportGenerationRequest[]) => {
        try {
            if (_allReports.length > 0) {
                setReportGenerationProgress('Finalizing reports...');

                const reportResult = await generateReport(_allReports);

                if (reportResult.success) {
                    const _filename =
                        _allReports.length === 1 ? _allReports[0].options.fileName : `${vessel?.vesselName || 'Vessel'}-Combined-Reports`;
                    const fullFilename = `${_filename}.${reportResult.fileExtension}`;

                    try {
                        // Always create the download link
                        const _downloadLink = document.createElement('a');
                        _downloadLink.href = reportResult.dataUri;
                        _downloadLink.download = fullFilename;
                        _downloadLink.style.display = 'none';
                        document.body.appendChild(_downloadLink);

                        const isExcelFile = reportResult.fileExtension === 'xlsx';

                        // Determine action based on selectedOutputAction and file type
                        if (isExcelFile || selectedOutputAction === 'Download') {
                            // Excel files can only be downloaded, not opened or printed directly
                            _downloadLink.click();
                        } else if (reportResult.fileExtension === 'pdf') {
                            // For PDF files, follow the selected action
                            if (selectedOutputAction === 'Open' || selectedOutputAction === 'Print') {
                                // Convert base64 to binary for the Blob
                                // Remove data URI prefix if present (e.g., "data:application/pdf;base64,")
                                const base64Data = reportResult.reportBase64 || reportResult.dataUri.split(',')[1] || reportResult.dataUri;

                                // Convert base64 to binary
                                const binaryString = window.atob(base64Data);
                                const byteArray = new Uint8Array(binaryString.length);

                                for (let i = 0; i < binaryString.length; i++) {
                                    byteArray[i] = binaryString.charCodeAt(i);
                                }

                                const blob = new Blob([byteArray], { type: 'application/pdf' });
                                const blobUrl = URL.createObjectURL(blob);

                                const printWindow = window.open(blobUrl, '_blank');

                                if (printWindow) {
                                    if (selectedOutputAction === 'Print') {
                                        // If Print is selected, trigger the print dialog
                                        printWindow.addEventListener('load', () => {
                                            printWindow.print();
                                            // Clean up the blob URL after a delay to ensure it's used
                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
                                        });

                                        // Fallback if load event doesn't fire
                                        setTimeout(() => {
                                            if (printWindow && !printWindow.closed) {
                                                printWindow.print();
                                                // Clean up the blob URL
                                                setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
                                            }
                                        }, 3000);
                                    } else {
                                        // For 'Open' action, just open in new window and clean up later
                                        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
                                    }
                                } else {
                                    toastError('Popup was blocked or failed to open. The report has been downloaded instead.');
                                    // Fallback to download if window can't be opened
                                    _downloadLink.click();
                                    // Clean up the blob URL since it won't be used
                                    URL.revokeObjectURL(blobUrl);
                                }
                            }
                        }

                        document.body.removeChild(_downloadLink);
                    } catch (downloadError) {
                        console.error('Error handling file download or print:', downloadError);
                        // Fallback to basic download only
                        const _downloadLink = document.createElement('a');
                        _downloadLink.href = reportResult.dataUri;
                        _downloadLink.download = fullFilename;
                        _downloadLink.click();
                    }
                } else {
                    console.error('Failed to generate report:', reportResult.error);
                    toastError(`Failed to generate report: ${reportResult.error || 'Unknown error'}`);
                }
            }
        } catch (error) {
            console.error('Error generating reports:', error);
            toastError(`Error generating reports: ${error.message || 'Unknown error'}`);
        } finally {
            setIsGeneratingReports(false);
            onClose();
        }
    };

    const toggleReportSelection = (reportSubType: ReportSubType): void => {
        const _newSelection = new Set(selectedReports);
        const isExcelReport = REPORT_TYPE_FILE_FORMAT[reportSubType.reportType] === 'xlsx';
        setSelectedReportFileType(isExcelReport ? 'xlsx' : 'pdf');

        if (_newSelection.has(reportSubType)) {
            _newSelection.delete(reportSubType);

            if (isExcelReport) {
                setSelectedReportFileType(null);
            }
        } else {
            _newSelection.add(reportSubType);
        }

        if (_newSelection.size === 0) {
            setSelectedReportFileType(null);
        }

        setSelectedReports(_newSelection);
    };

    // Fully clears the report selection, used to unblock a tab whose options are all disabled
    // because of a PDF/Excel format conflict with a selection made on a different tab.
    const clearAllReportSelections = (): void => {
        setSelectedReports(new Set());
        setSelectedReportFileType(null);
    };

    const groupedReportTypes = ReportTypes.reduce((_accumulator, reportType) => {
        const reportSubTypesForType = ReportSubTypes.filter((subType) => subType.reportType === reportType.reportType);

        if (reportSubTypesForType.length > 0) {
            _accumulator[reportType.reportType] = reportSubTypesForType;
        }

        return _accumulator;
    }, {} as Record<REPORT_TYPE, ReportSubType[]>);

    const panelDimensions: PanelDimensions = {
        width: PANEL_CONSTANTS.LARGE_WIDTH,
        height: 350,
    };

    const panelPosition = usePositionFromClick(mouseClickPosition, panelDimensions);

    const isGenerateButtonDisabled = () => {
        if (!selectedReports || selectedReports.size === 0 || isGeneratingReports) return true;

        return false;
    };

    return (
        <OdfjellPanel
            headerTitle="Generate Report"
            defaultPosition="right"
            initialPosition={mouseClickPosition ? panelPosition : undefined}
            panelWidth={PANEL_CONSTANTS.LARGE_WIDTH}
            preventOutsideClose={false}
            onCancel={onClose}
            onAction={handleGenerateReport}
            showCancelButton={true}
            actionButtonText={'Generate selected'}
            cancelButtonText="Cancel"
            isActionDisabled={isGenerateButtonDisabled()}
        >
            <>
                {isGeneratingReports && <ReportGenerationProgress progressMessage={reportGenerationProgress} />}
                <>
                    <ReportOptionsSection
                        groupedReportTypes={groupedReportTypes}
                        selectedReports={selectedReports}
                        toggleReportSelection={toggleReportSelection}
                        selectedReportFileType={selectedReportFileType}
                        lastCargoesNumberOfSteps={lastCargoesNumberOfSteps}
                        setLastCargoesNumberOfSteps={setLastCargoesNumberOfSteps}
                        selectedOutputAction={selectedOutputAction}
                        setSelectedOutputAction={setSelectedOutputAction}
                        cargoNameType={cargoNameType}
                        setCargoNameType={setCargoNameType}
                        includeUnNo={includeUnNo}
                        setIncludeUnNo={setIncludeUnNo}
                        includeAllTanks={includeAllTanks}
                        setIncludeAllTanks={setIncludeAllTanks}
                        manualCargoesOnly={manualCargoesOnly}
                        setManualCargoesOnly={setManualCargoesOnly}
                        tanksWithManualCargoes={tanksWithManualCargoes}
                        selectedStowageRotationPortCall={selectedStowageRotationPortCall}
                        selectedCharterers={selectedCharterers}
                        setSelectedCharterers={setSelectedCharterers}
                        selectedTanks={selectedTanks}
                        filterOnSelectedTanks={filterOnSelectedTanks}
                        setFilterOnSelectedTanks={setFilterOnSelectedTanks}
                        selectedBerthReportsBerthCall={selectedBerthReportsBerthCall}
                        setSelectedBerthReportsBerthCall={setSelectedBerthReportsBerthCall}
                        clearAllReportSelections={clearAllReportSelections}
                    />
                </>
            </>
        </OdfjellPanel>
    );
};

export default PageHeaderStowagePrintPanel;
